import PropTypes from 'prop-types';

/**
 * ProgressButton — premium dark-navy submit control with an inline 4px
 * progress bar along the bottom and a phase-cycling label.
 *
 * Designed for the login screen but reusable anywhere a long-running
 * action benefits from "progress + phase" feedback (account creation,
 * import jobs, etc.). Caller drives the visible state via:
 *
 *   - `phase`        — 'idle' | 'submitting' | 'finalizing' | 'success' | 'error'
 *   - `phaseLabels`  — map of phase → label string (label changes are
 *                      announced to assistive tech via aria-live)
 *   - `progress`     — optional 0..100. When omitted while loading, the
 *                      bar renders an indeterminate shimmer.
 *
 * Reduced-motion users see the same chrome with the shimmer keyframe
 * disabled (handled in CSS via @media (prefers-reduced-motion: reduce)).
 */
const DEFAULT_LABELS = {
  idle: 'Sign in',
  submitting: 'Signing you in…',
  finalizing: 'Loading your dashboard…',
  success: 'Welcome back',
  error: 'Try again',
};

export default function ProgressButton({
  phase = 'idle',
  phaseLabels,
  progress,
  type = 'submit',
  className = '',
  disabled = false,
  onClick,
  trailingIcon,
}) {
  const labels = { ...DEFAULT_LABELS, ...(phaseLabels || {}) };
  const isBusy = phase === 'submitting' || phase === 'finalizing';
  const isFinal = phase === 'success' || phase === 'error';
  const label = labels[phase] || labels.idle;

  const determinate = typeof progress === 'number' && Number.isFinite(progress);
  const clamped = determinate ? Math.max(0, Math.min(100, progress)) : null;

  const isDisabled = disabled || isBusy;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={isBusy || undefined}
      data-phase={phase}
      className={`txnweb-progress-button ${className}`.trim()}
    >
      <span className="txnweb-progress-button-label" aria-live="polite">
        {label}
      </span>
      {trailingIcon && !isBusy && !isFinal && (
        <span className="txnweb-progress-button-icon" aria-hidden>
          {trailingIcon}
        </span>
      )}
      <span
        className={`txnweb-progress-button-bar${
          isBusy ? ' is-busy' : ''
        }${determinate ? ' is-determinate' : ''}`}
        aria-hidden
      >
        <span
          className="txnweb-progress-button-bar-fill"
          style={determinate ? { width: `${clamped}%` } : undefined}
        />
      </span>
    </button>
  );
}

ProgressButton.propTypes = {
  phase: PropTypes.oneOf(['idle', 'submitting', 'finalizing', 'success', 'error']),
  phaseLabels: PropTypes.object,
  progress: PropTypes.number,
  type: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  trailingIcon: PropTypes.node,
};
